import { Fragment, useId } from "react";
import { EyeInvisibleOutlined, EyeOutlined } from "@ant-design/icons";
import { usePlotOutline } from "../../hooks/usePlotOutline";
import useSeedTranslations from "../../hooks/useSeedTranslations";
import seedRateDisplayUnit from "../../utils/seedRateDisplayUnit";
import SeedRateInput, { SEED_RATE_CONTROLS } from "../SeedRateInput";
import "./style.less";

export interface ManualProductPlot {
  id: string;
  group: string;
  plot: number;
  seedRate: number | null;
}

interface ManualProductPlotsProps {
  plots: ManualProductPlot[];
  errors?: Record<string, string | undefined>;
  onRateChange: (id: string, value: number | null) => void;
  focusedId?: string | null;
  onToggleFocus?: (id: string) => void;
  unit?: string;
  rateOptions?: number[];
}

const ManualProductPlots = ({
  plots,
  errors = {},
  onRateChange,
  onToggleFocus,
  unit = "s/ha",
  rateOptions,
}: ManualProductPlotsProps): JSX.Element => {
  const text = useSeedTranslations();
  const instanceId = useId();
  const { isOutlined, toggleOutline } = usePlotOutline();

  if (plots.length === 0) {
    return (
      <p className="rd-manual-plots__empty">{text("selectPlotsInstruction")}</p>
    );
  }

  const displayUnit = seedRateDisplayUnit(unit);

  return (
    <div className="rd-manual-plots" data-testid="manual-product-plots">
      <table className="rd-manual-plots__table">
        <colgroup>
          <col className="rd-manual-plots__col--group" />
          <col className="rd-manual-plots__col--plot" />
          <col className="rd-manual-plots__col--rate" />
          <col className="rd-manual-plots__col--eye" />
        </colgroup>

        <thead>
          <tr>
            <th scope="col">{text("group")}</th>
            <th scope="col">{text("plot")}</th>
            <th scope="col">
              {text("seedRate")} <span>({displayUnit})</span>
            </th>
            <th scope="col">
              <span className="rd-manual-plots__sr-only">
                {text("viewPlot")}
              </span>
            </th>
          </tr>
        </thead>

        <tbody>
          {plots.map((plot) => {
            const error = errors[plot.id];
            const errorId = `${instanceId}-${plot.id}-rate-error`;
            const rowHeaderId = `${instanceId}-${plot.id}-group`;
            const accentClass = `rd-manual-plots__accent--${plot.group}`;

            return (
              <Fragment key={plot.id}>
                <tr
                  className={`rd-manual-plots__row ${accentClass} ${
                    error ? "rd-manual-plots__row--has-error" : ""
                  }`}
                >
                  <th id={rowHeaderId} scope="row">
                    {plot.group}
                  </th>

                  <td>{plot.plot}</td>

                  <td>
                    <SeedRateInput
                      controls={SEED_RATE_CONTROLS}
                      options={rateOptions}
                      aria-label={text("plotRateAria", {
                        group: plot.group,
                        plot: plot.plot,
                      })}
                      value={plot.seedRate}
                      status={error ? "error" : undefined}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? errorId : undefined}
                      onChange={(value) => {
                        onRateChange(plot.id, value);
                      }}
                    />
                  </td>

                  <td>
                    <button
                      type="button"
                      className="rd-manual-plots__eye"
                      aria-label={text("highlightPlotAria", {
                        group: plot.group,
                        plot: plot.plot,
                      })}
                      aria-pressed={isOutlined(plot.id)}
                      onClick={() => {
                        toggleOutline(plot.id);
                        onToggleFocus?.(plot.id);
                      }}
                    >
                      {isOutlined(plot.id) ? (
                        <EyeOutlined aria-hidden style={{ fontSize: 22 }} />
                      ) : (
                        <EyeInvisibleOutlined
                          aria-hidden
                          style={{ fontSize: 22 }}
                        />
                      )}
                    </button>
                  </td>
                </tr>

                {error && (
                  <tr className={`rd-manual-plots__error-row ${accentClass}`}>
                    <td colSpan={4} headers={rowHeaderId}>
                      <span
                        id={errorId}
                        className="rd-manual-plots__error"
                        role="alert"
                      >
                        {error}
                      </span>
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};

export default ManualProductPlots;
